import { Loader2Icon } from 'lucide-react'

import { useGetRoomByAssessmentId, useSubmitAssessmentForApproval } from '@/api/assessments.hook'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'

import { controlStatusLabel } from '@/const/label'

import { StatCard, StatCardProps } from '@/pages/assessment/components/stat-card'
import { useControlCountStats } from '@/pages/assessment/use-control-count-stats'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Loading } from '@/components/lemmala-design-system/loading/loading'
import { toast } from '@/components/lemmala-design-system/toast/use-toast'
import { CollaborativeEditor } from '@/components/shared/rich-text-editor/collaborative-editor'
import { NotesProvider } from '@/components/shared/rich-text-editor/notes-provider'

import { CONCLUSION_DRAFT_VIEW_LABELS } from './labels'

const controlStatusToStatCardVariant: Record<ControlStatus, StatCardProps['variant']> = {
  [ControlStatus.GAP]: 'danger',
  [ControlStatus.VALIDATED]: 'success',
  [ControlStatus.NO_EVIDENCE]: 'warning',
  [ControlStatus.NOT_EVALUATED]: 'warning',
  [ControlStatus.ATTESTED]: 'success',
  [ControlStatus.NOT_APPLICABLE]: undefined,
  [ControlStatus.UNSPECIFIED]: undefined,
}

export type ConclusionDraftViewProps = {
  assessmentId: string
  companyId: string
  readonly: boolean
  onNext: () => void
  inAwaitingApproval: boolean
}

// ConclusionDraftView renders the IN_PROGRESS conclusion step.
export const ConclusionDraftView = ({
  assessmentId,
  companyId,
  readonly,
  onNext,
  inAwaitingApproval,
}: ConclusionDraftViewProps) => {
  const { isLoading, roomId } = useGetRoomByAssessmentId(assessmentId)

  const { mutateAsync: submitForApproval, isPending: isSubmitting } =
    useSubmitAssessmentForApproval(companyId)

  if (isLoading || !roomId) {
    return <Loading />
  }

  const handleSubmitForApproval = async () => {
    try {
      await submitForApproval({ ongoingAssessmentId: assessmentId })
      onNext?.()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: CONCLUSION_DRAFT_VIEW_LABELS.SUBMIT_ERROR_TITLE,
        description:
          error instanceof Error
            ? error.message
            : CONCLUSION_DRAFT_VIEW_LABELS.SUBMIT_ERROR_DESCRIPTION,
      })
    }
  }

  return (
    <div className='flex h-full min-h-0 flex-col'>
      <ConclusionStatsContainer assessmentId={assessmentId} />
      <div className='mt-8 flex min-h-36 flex-1 flex-col'>
        <div className='min-w-0 flex-1 overflow-hidden bg-main-background-card'>
          <NotesProvider roomId={roomId} key={roomId}>
            <CollaborativeEditor readonly={readonly} />
          </NotesProvider>
        </div>
      </div>
      {!readonly && (
        <div className='my-6 flex items-center justify-end gap-2'>
          {inAwaitingApproval ? (
            <Button variant={'outline'} onClick={onNext}>
              {CONCLUSION_DRAFT_VIEW_LABELS.NEXT}
            </Button>
          ) : (
            <Button onClick={handleSubmitForApproval} disabled={isSubmitting}>
              {isSubmitting && <Loader2Icon className='mr-2 size-4 animate-spin' />}
              {CONCLUSION_DRAFT_VIEW_LABELS.SUBMIT_FOR_APPROVAL}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

type ConclusionStatsContainerProps = {
  assessmentId?: OngoingAssessment['id']
}

const ConclusionStatsContainer = ({ assessmentId }: ConclusionStatsContainerProps) => {
  const controlCountStats = useControlCountStats(assessmentId)

  return (
    <div className='flex items-center gap-2 text-main-foreground-tertiary text-label-default'>
      {CONCLUSION_DRAFT_VIEW_LABELS.CONTROLS}
      {controlCountStats.map((stat) => (
        <StatCard
          key={stat.status}
          count={stat.count}
          label={controlStatusLabel[stat.status]}
          variant={controlStatusToStatCardVariant[stat.status]}
        />
      ))}
    </div>
  )
}
